import {
  Button,
  Modal,
  ModalHeader,
  Typography,
  FamilyChildProfile,
  JoonUIColor,
  TextButton,
  Checkbox,
} from "@joonapp/web-shared"
import { useMemo, useState } from "react"
import { create } from "zustand"

import { QUERY_KEYS } from "../../../constants"
import {
  useFamilyQuery,
  useTherapistInvitesQuery,
  useTherapistsQuery,
} from "../../../networking/queries"
import { sendTherapistInvitation } from "../../../networking/user"
import { FamilyQuerySelect, TherapistProfile } from "../../../types"
import { createJoonAPIClient } from "../../../util/joon-api"
import { queryClient } from "../../../util/queryClient"
import { useRouter } from "../../../util/router"
import { getChildImage } from "../../../util/util"

interface ManageTherapistModalStore {
  isOpen: boolean
  onOpen: (therapist: TherapistProfile) => void
  onClose: () => void
  therapist: TherapistProfile | null

  isUnlinkTherapistModalOpen: boolean
  openUnlinkTherapistModal: () => void
  closeUnlinkTherapistModal: () => void

  isUnlinkChildModalOpen: boolean
  openUnlinkChildModal: (child: FamilyChildProfile) => void
  closeUnlinkChildModal: () => void
  selectedChild: FamilyChildProfile | null

  isAddMoreChildrenModalOpen: boolean
  openAddMoreChildrenModal: () => void
  closeAddMoreChildrenModal: () => void
}

export const useManageTherapistModalStore = create<ManageTherapistModalStore>(
  (set) => ({
    isOpen: true,
    onOpen: (therapist) => set({ isOpen: true, therapist }),
    onClose: () => set({ isOpen: false, therapist: null }),
    therapist: null,

    isUnlinkTherapistModalOpen: false,
    openUnlinkTherapistModal: () => set({ isUnlinkTherapistModalOpen: true }),
    closeUnlinkTherapistModal: () => set({ isUnlinkTherapistModalOpen: false }),

    isUnlinkChildModalOpen: false,
    openUnlinkChildModal: (child) =>
      set({ isUnlinkChildModalOpen: true, selectedChild: child }),
    closeUnlinkChildModal: () =>
      set({ isUnlinkChildModalOpen: false, selectedChild: null }),
    selectedChild: null,

    isAddMoreChildrenModalOpen: false,
    openAddMoreChildrenModal: () => set({ isAddMoreChildrenModalOpen: true }),
    closeAddMoreChildrenModal: () => set({ isAddMoreChildrenModalOpen: false }),
  })
)

export const useTherapistPatients = (
  therapistProfileId: number | undefined
) => {
  const { data: therapists } = useTherapistsQuery()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)

  const linkedChildProfiles = useMemo(() => {
    const patientUserIds = therapists
      ?.find((t) => t.user.id === therapistProfileId)
      ?.patients.map(Number)
    return children?.filter((child) =>
      patientUserIds?.includes(child.user.id)
    ) as FamilyChildProfile[]
  }, [children, therapists, therapistProfileId])

  if (!linkedChildProfiles) return []

  return linkedChildProfiles
}

export const ManageTherapistModal = () => {
  const {
    isOpen,
    onClose,
    therapist,
    openUnlinkTherapistModal,
    openAddMoreChildrenModal,
  } = useManageTherapistModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const linkedChildProfiles = useTherapistPatients(therapist?.user.id)
  const { data: therapistInviteGroups } = useTherapistInvitesQuery()
  const childInvites = therapistInviteGroups?.find((group) =>
    group.some((invite) => invite.invitee_email === therapist?.user.email)
  )
  const invitedChildProfiles = useMemo(() => {
    const profiles: FamilyChildProfile[] = []
    childInvites?.forEach((invite) => {
      const profile = children?.find(
        (child) => child.user.id === invite.subject_user.id
      )
      if (profile) profiles.push(profile)
    })
    return profiles
  }, [childInvites, children])

  if (!children || !therapist) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      animate
      style={{ width: "min(450px, 95vw)" }}
    >
      <ModalHeader
        title="Manage Therapist"
        onClose={onClose}
        showBorderBottom
      />
      <div className="flex flex-col gap-2 p-6 pt-4 bg-bgXLightGray">
        <Typography variant="body">Currently linked with:</Typography>
        {linkedChildProfiles.map((patient) => (
          <LinkedPatientItem key={patient.user.id} child={patient} />
        ))}
        {invitedChildProfiles &&
          invitedChildProfiles?.length > 0 &&
          invitedChildProfiles.map((child) => (
            <LinkedPatientItem key={child.user.id} child={child} isInvite />
          ))}
        {linkedChildProfiles.length < children.length && (
          <Button
            buttonType="secondary"
            text="+ Link more"
            onClick={openAddMoreChildrenModal}
            size="small"
            fullWidth
          />
        )}
        <TextButton
          onClick={openUnlinkTherapistModal}
          className="w-fit mx-auto mt-4"
        >
          <Typography
            variant="bodySmall"
            color={JoonUIColor.semantic.destructive}
          >
            Remove {therapist.nickname || therapist.user.name}
          </Typography>
        </TextButton>
      </div>
      <UnlinkTherapistModal />
      <UnlinkChildModal />
      <AddMoreChildrenModal />
    </Modal>
  )
}

const LinkedPatientItem = ({
  child,
  isInvite = false,
}: {
  child: FamilyChildProfile
  isInvite?: boolean
}) => {
  const childImage = getChildImage(child.user)
  const { openUnlinkChildModal } = useManageTherapistModalStore()

  return (
    <div className="flex items-center gap-2 border border-borderPrimary rounded-md px-4 py-3 bg-white">
      <div className="flex gap-2 mr-auto items-center">
        <img
          src={childImage}
          alt={child.user.name}
          className="w-6 h-6 rounded-full"
        />
        <Typography variant="body">{child.user.name}</Typography>
      </div>

      {isInvite ? (
        <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
          Pending
        </Typography>
      ) : (
        <button onClick={() => openUnlinkChildModal(child)}>
          <Typography
            variant="bodySmall"
            color={JoonUIColor.text.primaryAccent}
          >
            Unlink
          </Typography>
        </button>
      )}
    </div>
  )
}

const UnlinkTherapistModal = () => {
  const {
    isUnlinkTherapistModalOpen,
    closeUnlinkTherapistModal,
    therapist,
    onClose,
  } = useManageTherapistModalStore()
  const [isUnlinkingTherapist, setIsUnlinkingTherapist] = useState(false)
  const linkedChildProfiles = useTherapistPatients(therapist?.user.id)

  const router = useRouter()

  const unlinkAllChildren = async () => {
    const API = createJoonAPIClient()
    try {
      setIsUnlinkingTherapist(true)
      const promises = linkedChildProfiles.map((child) =>
        API.post(`api/users/${child.user.id}/unlink-therapist/`, {
          therapist_id: therapist?.user.id,
        })
      )
      await Promise.all(promises)
      await Promise.all([
        queryClient.invalidateQueries([QUERY_KEYS.THERAPISTS]),
        queryClient.invalidateQueries([QUERY_KEYS.CARE_TEAM]),
        queryClient.invalidateQueries([QUERY_KEYS.PARENT_TASKS]),
        queryClient.invalidateQueries([QUERY_KEYS.TRAININGS]),
        queryClient.invalidateQueries([QUERY_KEYS.PARENT_TASK_INSTANCES]),
      ])
      onClose()
      closeUnlinkTherapistModal()
      router.push("/care")
    } catch (error) {
      throw new Error("Failed to unlink children")
    } finally {
      setIsUnlinkingTherapist(false)
    }
  }

  if (!therapist) return null

  return (
    <Modal
      isOpen={isUnlinkTherapistModalOpen}
      onClose={closeUnlinkTherapistModal}
      style={{ width: "min(400px, 95vw)" }}
    >
      <ModalHeader
        title="Are you sure ?"
        onClose={closeUnlinkTherapistModal}
        showBorderBottom
      />
      <div className="flex flex-col p-6 pt-4 gap-6">
        <Typography variant="body">
          To re-link in the future, you will need to get{" "}
          {therapist.nickname || therapist.user.name}'s code again.
        </Typography>
        <Button
          buttonType="redPrimary"
          onClick={unlinkAllChildren}
          text={`Remove ${therapist.nickname || therapist.user.name}`}
          fullWidth
          isLoading={isUnlinkingTherapist}
        />
      </div>
    </Modal>
  )
}

const UnlinkChildModal = () => {
  const [isUnlinkingChild, setIsUnlinkingChild] = useState(false)
  const {
    isUnlinkChildModalOpen,
    closeUnlinkChildModal,
    selectedChild,
    therapist,
    onClose: closeManageTherapistModal,
  } = useManageTherapistModalStore()
  const router = useRouter()

  const linkedChildProfiles = useTherapistPatients(therapist?.user.id)
  const isLastChild = linkedChildProfiles.length === 1

  const unlinkChild = async () => {
    const API = createJoonAPIClient()
    try {
      setIsUnlinkingChild(true)
      await API.post(`api/users/${selectedChild?.user.id}/unlink-therapist/`, {
        therapist_id: therapist?.user.id,
      })
      await Promise.all([
        queryClient.invalidateQueries([QUERY_KEYS.THERAPISTS]),
        queryClient.invalidateQueries([QUERY_KEYS.CARE_TEAM]),
      ])
      closeUnlinkChildModal()

      if (isLastChild) {
        closeManageTherapistModal()
        router.push("/care")
        queryClient.invalidateQueries([QUERY_KEYS.PARENT_TASKS])
        queryClient.invalidateQueries([QUERY_KEYS.TRAININGS])
        queryClient.invalidateQueries([QUERY_KEYS.PARENT_TASK_INSTANCES])
      }
    } catch (error) {
      throw new Error("Failed to unlink child")
    } finally {
      setIsUnlinkingChild(false)
    }
  }

  return (
    <Modal
      isOpen={isUnlinkChildModalOpen}
      onClose={closeUnlinkChildModal}
      style={{ width: "min(400px, 95vw)" }}
    >
      <ModalHeader
        title="Unlink Child"
        onClose={closeUnlinkChildModal}
        showBorderBottom
      />
      <div className="flex flex-col p-6 pt-4 gap-6">
        {isLastChild ? (
          <Typography variant="body">
            This action will remove{" "}
            {therapist?.nickname || therapist?.user.name}
            's access to {selectedChild?.user.name}'s data and remove{" "}
            {therapist?.nickname || therapist?.user.name} from your therapist
            list.
            <br />
            To re-link them back in the future, you will need to re-request this
            connection again.
          </Typography>
        ) : (
          <Typography variant="body">
            This action will remove{" "}
            {therapist?.nickname || therapist?.user.name}
            's access to {selectedChild?.user.name}'s data.
            <br />
            To re-link them back in the future, you will need to re-request this
            connection again.
          </Typography>
        )}
        <div className="flex flex-row gap-2">
          <Button
            buttonType="secondary"
            onClick={closeUnlinkChildModal}
            text="No, cancel"
            style={{ flex: 0.4 }}
          />
          <Button
            buttonType="redPrimary"
            onClick={unlinkChild}
            text={isLastChild ? "Yes, unlink and remove" : "Yes, unlink"}
            isLoading={isUnlinkingChild}
            style={{ flex: 0.6 }}
          />
        </div>
      </div>
    </Modal>
  )
}

const AddMoreChildrenModal = () => {
  const [selectedChildren, setSelectedChildren] = useState<
    FamilyChildProfile[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isRequestSent, setIsRequestSent] = useState(false)
  const { isAddMoreChildrenModalOpen, closeAddMoreChildrenModal, therapist } =
    useManageTherapistModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)

  const linkedChildProfiles = useTherapistPatients(therapist?.user.id)

  const unlinkedChildren = children?.filter(
    (child) =>
      !linkedChildProfiles.some(
        (linkedChild) => linkedChild.user.id === child.user.id
      )
  )

  const handleLinkChildren = async () => {
    if (!therapist?.user.email) return
    if (selectedChildren.length === 0)
      setError("Please select at least one child")
    try {
      setIsLoading(true)
      await sendTherapistInvitation(
        therapist?.user.email,
        selectedChildren.map((child) => child.user.id)
      )
      await new Promise((resolve) => setTimeout(resolve, 100))
      await queryClient.invalidateQueries([QUERY_KEYS.THERAPIST_INVITES])
      setIsRequestSent(true)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleChild = (child: FamilyChildProfile) => {
    setSelectedChildren((prev) =>
      prev.includes(child)
        ? prev.filter((c) => c.user.id !== child.user.id)
        : [...prev, child]
    )
  }

  return (
    <Modal
      isOpen={isAddMoreChildrenModalOpen}
      onClose={closeAddMoreChildrenModal}
      style={{ width: "min(400px, 95vw)" }}
    >
      <ModalHeader
        title={
          isRequestSent
            ? `Request sent to ${therapist?.nickname || therapist?.user.name}!`
            : "Request to link more children"
        }
        onClose={closeAddMoreChildrenModal}
        showBorderBottom
      />
      {isRequestSent ? (
        <div className="flex flex-col p-6 pt-4 gap-6">
          <Typography variant="body">
            {therapist?.nickname || therapist?.user.name} should receive an
            email with this request and will be able to complete this process!
          </Typography>
          <Button
            buttonType="primary"
            onClick={closeAddMoreChildrenModal}
            text="Done"
            fullWidth
          />
        </div>
      ) : (
        <div className="flex flex-col p-6 pt-4 gap-6">
          <div className="flex flex-col gap-2">
            <Typography variant="body">Select child to link:</Typography>
            <div className="flex flex-col border border-borderPrimary rounded-md">
              {unlinkedChildren?.map((child, i) => (
                <button
                  key={child.user.id}
                  onClick={() => handleToggleChild(child)}
                >
                  <div
                    className={`flex flex-row items-center justify-between p-3 ${
                      i < unlinkedChildren.length - 1
                        ? "border-b border-borderPrimary"
                        : ""
                    }`}
                  >
                    <Typography variant="body">{child.user.name}</Typography>
                    <Checkbox
                      hideBorder
                      name={child.user.id.toString()}
                      selected={selectedChildren.includes(child)}
                      onChange={() => {}}
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
          {error && (
            <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
              {error}
            </Typography>
          )}
          <div className="flex flex-row gap-2">
            <Button
              buttonType="secondary"
              onClick={closeAddMoreChildrenModal}
              text="Cancel"
              style={{ flex: 0.4 }}
            />
            <Button
              buttonType="primary"
              onClick={handleLinkChildren}
              text="Send request"
              isLoading={isLoading}
              style={{ flex: 0.6 }}
            />
          </div>
        </div>
      )}
    </Modal>
  )
}
